// ============================================================
// E2：大屏编辑器 AI 面板（v1）
// 主题（整页）+ 选中组件替换/绑定/删除/添加；幽灵预览 → 应用/撤销/重新生成/丢弃
// 应用走 E0 命令总线（dashboard 手势批次 = 1 个撤销步）
// ============================================================
import { useState } from 'react';
import { Button, Drawer, Input, Segmented, Space, Tag, Tooltip, message } from 'antd';
import { RobotOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { commandBus } from '../../core/commandBus';
import useDashboardStore from '../../stores/dashboardStore';
import { useDashboardHistory } from '../../stores/dashboardHistory';
import { markAiEditorStatus, planAiEditor, type DashboardOp, type EditorPlanResponse } from '../../api/aiEditor';import { dashboardOpsToCommands, type DashboardOpCommandBuild } from '../../utils/dashboardAiOps';

export default function AiDashboardPanel() {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<'global' | 'selection'>('global');
  const [prompt, setPrompt] = useState('');
  const [planning, setPlanning] = useState(false);
  const [plan, setPlan] = useState<EditorPlanResponse | null>(null);
  const [build, setBuild] = useState<DashboardOpCommandBuild | null>(null);
  const [applied, setApplied] = useState(false);
  const selectedComponentId = useDashboardStore((s) => s.selectedComponentId);

  const generate = async () => {
    const text = prompt.trim();
    if (!text) {
      message.warning('请输入指令，如「换成浅色主题」「添加告警列表」');
      return;
    }
    const store = useDashboardStore.getState();
    if (scope === 'selection' && !store.selectedComponentId) {
      message.warning('请先在画布中选中组件');
      return;
    }
    setPlanning(true);
    try {
      const r = (await planAiEditor({
        prompt: text,
        target: 'dashboard',
        scope,
        components: store.components.map((c) => ({
          id: c.id,
          type: c.type,
          title: (c.base?.title as string) ?? undefined,
        })),
        pageConfig: {
          width: store.currentPage?.config?.width,
          height: store.currentPage?.config?.height,
          backgroundColor: store.currentPage?.config?.backgroundColor,
        },
        ...(scope === 'selection' && store.selectedComponentId ? { selectedComponentId: store.selectedComponentId } : {}),
      })) as EditorPlanResponse;
      setPlan(r);
      setBuild(
        dashboardOpsToCommands(r.ops as DashboardOp[], store.components, {
          width: store.currentPage?.config?.width,
          height: store.currentPage?.config?.height,
        }),
      );
      setApplied(false);
    } catch (e) {
      message.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'AI 计划生成失败');
    } finally {
      setPlanning(false);
    }
  };

  const apply = async () => {
    if (!plan || !build) return;
    const r = await commandBus.applyBatch(build.commands, { label: 'ai-dashboard' });
    if (!r.ok) {
      await markAiEditorStatus(plan.callId, { status: 'failed', ...(r.error ? { error: r.error } : {}) }).catch(() => undefined);
      message.error(r.error ?? '应用失败');
      return;
    }
    setApplied(true);
    await markAiEditorStatus(plan.callId, { status: 'applied' }).catch(() => undefined);
    message.success(`已应用 ${build.commands.length} 项操作（可撤销）`);
  };

  const undo = () => {
    useDashboardHistory.getState().undo();
    setApplied(false);
    message.success('已撤销 AI 变更');
  };

  const discard = async () => {
    if (plan) await markAiEditorStatus(plan.callId, { status: 'discarded' }).catch(() => undefined);
    setPlan(null);
    setBuild(null);
    setApplied(false);
  };

  return (
    <>
      <Tooltip title="AI 助手（主题 / 选中组件替换·绑定；预览后应用，可撤销）">
        <Button size="small" icon={<RobotOutlined />} onClick={() => setOpen(true)}>
          AI 助手
        </Button>
      </Tooltip>

      <Drawer title="AI 助手（大屏）" open={open} onClose={() => setOpen(false)} size={400} destroyOnHidden>
        <Space orientation="vertical" style={{ width: '100%' }} size={12}>
          <Segmented
            value={scope}
            onChange={(v) => setScope(v as 'global' | 'selection')}
            options={[
              { label: '整页', value: 'global' },
              { label: '选中组件', value: 'selection', disabled: !selectedComponentId },
            ]}
          />
          <Input.TextArea
            rows={3}
            placeholder="如：换成浅色主题 / 添加告警列表 / 把选中组件换成折线图 / 标题改为进出水流量"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
          <Space>
            <Button type="primary" icon={<ThunderboltOutlined />} loading={planning} onClick={() => void generate()}>
              生成计划
            </Button>
            {plan ? (
              <Button onClick={() => void generate()} loading={planning}>
                重新生成
              </Button>
            ) : null}
            {plan ? <Button onClick={() => void discard()}>丢弃</Button> : null}
          </Space>

          {plan ? (
            <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12, background: '#fafcff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <Tag color={plan.source === 'llm' ? 'geekblue' : 'orange'}>{plan.source === 'llm' ? 'AI 模型' : '规则兜底'}</Tag>
                <span style={{ fontSize: 12, color: '#595959' }}>{plan.summary}</span>
              </div>
              {build && build.descriptions.length > 0 ? (
                <ol style={{ margin: '4px 0 8px 18px', padding: 0, fontSize: 13 }}>
                  {build.descriptions.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ol>
              ) : (
                <div style={{ fontSize: 13, color: '#8c8c8c' }}>无可执行操作（换一种说法试试）</div>
              )}
              <Space>
                <Button type="primary" data-ai-action="apply" disabled={!build || build.commands.length === 0 || applied} onClick={() => void apply()}>
                  应用
                </Button>
                {applied ? (
                  <Button data-ai-action="undo" onClick={undo}>
                    撤销
                  </Button>
                ) : null}
              </Space>
            </div>
          ) : null}
        </Space>
      </Drawer>
    </>
  );
}
